import type { PathOptions, Map as LeafletMap } from 'leaflet';
import { geoJSON, featureGroup } from 'leaflet';
import { symmetricDifference } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';

import type { FeaturesInBounds } from '@/features/build-calculation-context/components/Home/components/GridImageLayer/FeaturePicker/FeaturePicker';
import { FeaturePicker } from '@/features/build-calculation-context/components/Home/components/GridImageLayer/FeaturePicker/FeaturePicker';
import { useFindFeaturesOnMapClick } from '@/features/build-calculation-context/components/Home/hooks/useFindFeaturesOnMapClick';
import type {
  DomainGuid,
  VoltageLevel
} from '@/features/execute-calculation-set/overmind/calculation';
import { useActions, useAppState } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type { TacticResultLayers } from '../hooks/TacticResultLayers';
import { UseLvTacticResultLayers } from '../hooks/useLvTacticResultLayers';
import { useMvTacticResultLayers } from '../hooks/useMvTacticResultLayers';

function useFeaturesInBounds(): FeaturesInBounds {
  const featuresInBoundsState = useAppState().arcgis.featuresInBounds;

  return featuresInBoundsState.current === 'LOADED'
    ? featuresInBoundsState.value
    : {
        features: []
      };
}

export function TacticResultInMap(props: { voltageLevel: VoltageLevel }) {
  const map = useMap();
  const { handleGridElementSelected } = useActions().calculation;

  const featuresInBounds = useFeaturesInBounds();
  const [tacticResultLayers] = useTacticResultLayers(props.voltageLevel, map);
  const [unloadFoundFeatures] = useFindFeaturesOnMapClick();

  useFitToBounds(tacticResultLayers);

  const onFeatureSelected = (newSelected: FeatureWithIDs) => {
    unloadFoundFeatures();
    handleGridElementSelected(newSelected.gc.globalId);
  };

  const featuresToPickFrom = {
    bounds: featuresInBounds.bounds,
    features: filterFeatures(
      featuresInBounds.features,
      tacticResultLayers.idsInLayers
    )
  };
  return (
    <FeaturePicker
      featuresToPickFrom={featuresToPickFrom}
      onSelected={onFeatureSelected}
      onClose={unloadFoundFeatures}
    />
  );
}
function useFitToBounds(tacticResultLayers: TacticResultLayers) {
  const backgroundMapReady = useAppState().map.backgroundMapReady;
  const map = useMap();
  const [currentIdsInMap, setCurrentIdsInMap] = useState<
    TacticResultLayers['idsInLayers']
  >([]);
  useEffect(() => {
    const idsAddedOrRemovedFromMap = symmetricDifference(
      currentIdsInMap,
      tacticResultLayers.idsInLayers
    );

    if (
      idsAddedOrRemovedFromMap.length > 0 &&
      tacticResultLayers.layers &&
      tacticResultLayers.layers.length > 0 &&
      backgroundMapReady === 'READY'
    ) {
      const bounds = featureGroup(tacticResultLayers.layers).getBounds();
      map.fitBounds(bounds);
      setCurrentIdsInMap(tacticResultLayers.idsInLayers);
    }
  }, [tacticResultLayers, backgroundMapReady]);
}

function filterFeatures(
  featuresToFilter: FeatureWithIDs[],
  filterIds: DomainGuid[] | undefined
) {
  return featuresToFilter.filter(feature =>
    filterIds?.includes(feature.gc.globalId)
  );
}

function useTacticResultLayers(
  voltageLevel: VoltageLevel,
  map: LeafletMap
): [TacticResultLayers] {
  const handleGridElementSelected =
    useActions().calculation.handleGridElementSelected;
  switch (voltageLevel) {
    case 'LowVoltage':
      return UseLvTacticResultLayers(map);
    case 'MediumVoltage':
      return useMvTacticResultLayers(map, handleGridElementSelected);
    default:
      throw new Error(
        `Unsupported voltage level when using tactic result layer: ${voltageLevel}`
      );
  }
}
